/**
 * The service map (design "Traces: service map"): the services, the Kafka topics, the datastores (dashed) and outside
 * hosts as boxes, who calls whom as arrows (an SVG layer under them) labelled in words ("2 routes · 6 calls"; hover or
 * focus one for each route with its calls, p95 and failures), failed calls red and dashed with "N failed calls". The
 * browser is not a box of its own (2026-10-30): the frontend that served the page is, "in the browser", with its page
 * loads inside it, and the requests its page made start at it (vfs-service traces/browserOrigin.js); only requests with
 * no known frontend (curl, API clients) have the "Visitors / other clients" box. No arrow without calls; a box with
 * nothing in the range is dimmed. A request that never reached its service (the preview proxy answered by itself)
 * draws a red arrow with the latest reason ("not reached: it crashed"), or turns a page's box red. Laid out by
 * mapLayout.js for the box's width. Each box and each arrow's label is a button: picking one finds its requests
 * (`onPick(label)`). On a phone (the panel under 720 px) the same
 * arrows as a list. A new request of the live refresh (`arrivals: [{ key, hops }]`, mapFlights.js) flies over its
 * arrows as a small dot (red for a failed step), each box it reaches pulsing; with reduced motion the arrow only lights
 * up for a moment.
 */
import React, { useEffect, useId, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { layoutMap } from './mapLayout';
import { HOP_MS, emptyFlights, enqueueFlights, tickFlights } from './mapFlights';
import { edgeErrors, edgeLabel, edgeReason, edgeRoutes, edgeRows, mapView, nodeDescription, nodeSublabel } from './tracesModel';

function useWidth(ref, fallback = 880) {
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = ref.current;
    if (!node) { return undefined; }
    const measure = () => { if (node.clientWidth > 0) { setWidth(node.clientWidth); } };
    measure();
    if (typeof ResizeObserver === 'undefined') { return undefined; }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

const reducedMotion = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function flightsReducer(state, action) {
  if (action.type === 'add') { return enqueueFlights(state, action.flights); }
  if (action.type === 'tick') { return tickFlights(state); }
  return state;
}

function useFlights(arrivals) {
  const [flights, dispatch] = useReducer(flightsReducer, undefined, emptyFlights);
  useEffect(() => { if (arrivals?.length) { dispatch({ type: 'add', flights: arrivals }); } }, [arrivals]);
  const flying = flights.active.length > 0;
  useEffect(() => {
    if (!flying) { return undefined; }
    const timer = setInterval(() => dispatch({ type: 'tick' }), HOP_MS);
    return () => clearInterval(timer);
  }, [flying]);
  return flights.active;
}

export default function ServiceMap({ map: given, picked = '', onPick = null, arrivals = null }) {
  const map = React.useMemo(() => mapView(given), [given]);
  const flights = useFlights(arrivals);
  const still = reducedMotion();
  const boxRef = useRef(null);
  const width = useWidth(boxRef);
  const markerBase = `tr-ah-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const nodes = map?.nodes || [];
  const edges = map?.edges || [];
  const layout = React.useMemo(() => layoutMap(nodes, edges, { width }), [nodes, edges, width]);
  const labels = new Map(nodes.map((n) => [n.id, n.label]));
  const pick = (label) => onPick?.(picked === label ? '' : label);

  return (
    <section aria-label="Service map" className="traces-card traces-map" data-testid="traces-map">
      <div className="traces-map-scroll" ref={boxRef}>
        <div className="traces-map-canvas" style={{ width: layout.width, height: layout.height }}>
          <svg aria-hidden="true" className="traces-map-edges" height={layout.height} width={layout.width}>
            <defs>
              <marker id={`${markerBase}-ok`} markerHeight="9" markerUnits="userSpaceOnUse" markerWidth="9" orient="auto" refX="7" refY="4" viewBox="0 0 8 8">
                <path className="traces-arrowhead" d="M0 0 8 4 0 8z" />
              </marker>
              <marker id={`${markerBase}-bad`} markerHeight="9" markerUnits="userSpaceOnUse" markerWidth="9" orient="auto" refX="7" refY="4" viewBox="0 0 8 8">
                <path className="traces-arrowhead is-bad" d="M0 0 8 4 0 8z" />
              </marker>
            </defs>
            {layout.edges.map((e) => (
              <line
                className={`traces-edge${e.errors > 0 ? ' is-bad' : ''}`}
                data-testid={`traces-edge-${e.id}`}
                key={e.id}
                markerEnd={`url(#${markerBase}-${e.errors > 0 ? 'bad' : 'ok'})`}
                strokeWidth={e.stroke}
                x1={e.x1}
                x2={e.x2}
                y1={e.y1}
                y2={e.y2}
              />
            ))}
            {flights.map((f) => {
              const hop = f.hops[f.at];
              const e = layout.edges.find((x) => x.id === hop?.edgeId);
              if (!e) { return null; }
              const key = `${f.key}:${f.at}`;
              return still
                ? <line className={`traces-flash${hop.bad ? ' is-bad' : ''}`} data-testid="traces-flight" key={key} x1={e.x1} x2={e.x2} y1={e.y1} y2={e.y2} />
                : (
                  <circle className={`traces-dot${hop.bad ? ' is-bad' : ''}`} cx={0} cy={0} data-testid="traces-flight" key={key} r={4.5}>
                    <animateMotion dur={`${HOP_MS - 50}ms`} fill="freeze" path={`M${e.x1},${e.y1} L${e.x2},${e.y2}`} />
                  </circle>
                );
            })}
          </svg>
          {!still && flights.map((f) => {
            const hop = f.hops[f.at];
            const n = hop && layout.nodes.find((x) => x.id === hop.to);
            if (!n) { return null; }
            return (
              <span
                aria-hidden
                className={`traces-pulse${hop.bad ? ' is-bad' : ''}`}
                key={`${f.key}:${f.at}:pulse`}
                style={{ left: n.x, top: n.y, width: n.w, height: n.h, animationDelay: `${HOP_MS - 100}ms` }}
              />
            );
          })}
          {layout.edges.map((e) => {
            const errors = edgeErrors(e);
            const reason = edgeReason(e);
            const to = labels.get(e.to) || e.to;
            const routes = edgeRoutes(e);
            const cardId = `${markerBase}-card-${e.id.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
            return (
              <button
                aria-describedby={routes.length ? cardId : undefined}
                aria-label={`${labels.get(e.from) || e.from} calls ${to}: ${edgeLabel(e)}${errors ? `, ${errors}` : ', none failed'}${reason ? `, ${reason}` : ''}. Find these requests`}
                aria-pressed={picked === to}
                className={`traces-elabel${errors ? ' is-bad' : ''}`}
                key={e.id}
                onClick={() => pick(to)}
                style={e.label ? { left: e.label.x, top: e.label.y, width: e.label.w, height: e.label.h } : { left: e.lx, top: e.ly }}
                type="button"
              >
                <span>{edgeLabel(e)}</span>
                {errors && <span className="traces-elabel-bad">{errors}</span>}
                {reason && <span className="traces-elabel-reason" data-testid={`traces-edge-reason-${e.id}`}>{reason}</span>}
                {/* Each route with its calls, p95 and failures: shown on hover and focus. */}
                {routes.length > 0 && (
                  <span className="traces-elabel-card" data-testid={`traces-edge-routes-${e.id}`} id={cardId} role="tooltip">
                    {routes.map((r) => (
                      <span className={`traces-elabel-route${r.bad ? ' is-bad' : ''}`} key={r.key}>
                        <strong>{r.name}</strong>{r.text && <span>{r.text}</span>}
                      </span>
                    ))}
                  </span>
                )}
              </button>
            );
          })}
          {layout.nodes.map((n) => {
            const sub = nodeSublabel(n);
            const store = n.kind === 'datastore' || n.kind === 'topic' || n.kind === 'external';
            return (
              <button
                aria-label={`${nodeDescription(n)}. Find its requests`}
                aria-pressed={picked === n.label}
                className={`traces-node is-${n.kind}${store ? ' is-store' : ''}${n.inBrowser ? ' is-in-browser' : ''}${n.idle ? ' is-idle' : ''}${n.errors > 0 || n.unreached > 0 || n.pageErrors > 0 ? ' is-bad' : ''}`}
                data-testid={`traces-node-${n.id}`}
                key={n.id}
                onClick={() => pick(n.label)}
                style={{ left: n.x, top: n.y, width: n.w, height: n.h }}
                title={n.lastReasonWords ? `Not reached: ${n.lastReasonWords}` : n.lastError ? `Last error: ${n.lastError}` : undefined}
                type="button"
              >
                <strong>{n.label}</strong>
                {sub.where && <small className="traces-node-where">{sub.where}</small>}
                <small className={sub.bad ? 'is-bad' : undefined}>{sub.text}</small>
              </button>
            );
          })}
        </div>
      </div>
      <ul aria-label="Who calls whom" className="traces-edge-list" data-testid="traces-edge-list">
        {edgeRows(map).map((row) => (
          <li key={row.id}>
            <button aria-pressed={picked === row.to} className={`traces-edge-row${row.errors ? ' is-bad' : ''}`} onClick={() => pick(row.to)} type="button">
              <span>{row.text}</span>
              {row.errors && <span className="traces-edge-row-bad">{row.errors}</span>}
              {edgeReason(row.edge) && <span className="traces-edge-row-bad">{edgeReason(row.edge)}</span>}
            </button>
          </li>
        ))}
      </ul>
      <p className="traces-footnote">
        Each box is a part of your app. A frontend&apos;s box is also the page open in the browser: its page loads are counted inside it, and the calls its page makes start from it. Arrows show who calls whom in this time range, with how many routes and calls (point at one to see each route); thicker means more calls. Red: calls that failed, with how many. A faded box had no requests.
      </p>
    </section>
  );
}

ServiceMap.propTypes = {
  // New requests to fly over the map: `[{ key, hops: [{ edgeId, from, to, bad }] }]` (mapFlights.tracePath).
  arrivals: PropTypes.array,
  map: PropTypes.shape({ nodes: PropTypes.array, edges: PropTypes.array }),
  onPick: PropTypes.func,
  picked: PropTypes.string,
};
